import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Notification } from "@/types/apps/notification";

interface NotificationsState {
	notifications: Notification[];
	unreadCount: number;
	loading: boolean;
	lastFetched: number | null;
	selectedNotification: Notification | null;
	filter: "all" | "unread" | "read";
	searchQuery: string;
}

const initialState: NotificationsState = {
	notifications: [],
	unreadCount: 0,
	loading: false,
	lastFetched: null,
	selectedNotification: null,
	filter: "all",
	searchQuery: "",
};

const notificationsSlice = createSlice({
	name: "notifications",
	initialState,
	reducers: {
		setNotifications: (state, action: PayloadAction<Notification[]>) => {
			state.notifications = action.payload;
			state.unreadCount = action.payload.filter(
				(n) => n.read_at === null,
			).length;
			state.lastFetched = Date.now();
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		markAsRead: (state, action: PayloadAction<string>) => {
			const notification = state.notifications.find(
				(n) => n.id === action.payload,
			);
			if (notification && notification.read_at === null) {
				notification.read_at = new Date().toISOString();
				state.unreadCount = Math.max(0, state.unreadCount - 1);
			}
			if (state.selectedNotification?.id === action.payload) {
				state.selectedNotification = {
					...state.selectedNotification,
					read_at: new Date().toISOString(),
				};
			}
		},
		markAsUnread: (state, action: PayloadAction<string>) => {
			const notification = state.notifications.find(
				(n) => n.id === action.payload,
			);
			if (notification && notification.read_at !== null) {
				notification.read_at = null;
				state.unreadCount = state.unreadCount + 1;
			}
			if (state.selectedNotification?.id === action.payload) {
				state.selectedNotification = {
					...state.selectedNotification,
					read_at: null,
				};
			}
		},
		setSelectedNotification: (
			state,
			action: PayloadAction<Notification | null>,
		) => {
			state.selectedNotification = action.payload;
		},
		setFilter: (state, action: PayloadAction<"all" | "unread" | "read">) => {
			state.filter = action.payload;
		},
		setSearchQuery: (state, action: PayloadAction<string>) => {
			state.searchQuery = action.payload;
		},
		clearNotifications: (state) => {
			state.notifications = [];
			state.unreadCount = 0;
			state.lastFetched = null;
			state.selectedNotification = null;
		},
	},
});

export const {
	setNotifications,
	setLoading,
	markAsRead,
	markAsUnread,
	setSelectedNotification,
	setFilter,
	setSearchQuery,
	clearNotifications,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
